import type { TimePreset, WeatherKind } from "../core/types";

export type Rgb = readonly [number, number, number];

export interface EnvironmentState {
  hour: number;
  sunElevation: number;
  sunAzimuth: number;
  sunIntensity: number;
  hemisphereIntensity: number;
  cloudCover: number;
  mist: number;
  rain: number;
  wetness: number;
  wind: number;
  fireflies: number;
  fogNear: number;
  fogFar: number;
  skyTop: Rgb;
  skyHorizon: Rgb;
  fogColor: Rgb;
  sunColor: Rgb;
  waterColor: Rgb;
}

interface WeatherProfile {
  cloudCover: number;
  mist: number;
  rain: number;
  wetness: number;
  wind: number;
  visibility: number;
  light: number;
}

const WEATHER: Record<WeatherKind, WeatherProfile> = {
  clear: { cloudCover: 0.12, mist: 0.08, rain: 0, wetness: 0.05, wind: 0.22, visibility: 1, light: 1 },
  cloudy: { cloudCover: 0.46, mist: 0.14, rain: 0, wetness: 0.12, wind: 0.32, visibility: 0.9, light: 0.86 },
  overcast: { cloudCover: 0.82, mist: 0.28, rain: 0, wetness: 0.25, wind: 0.36, visibility: 0.73, light: 0.66 },
  mist: { cloudCover: 0.66, mist: 0.82, rain: 0, wetness: 0.38, wind: 0.12, visibility: 0.38, light: 0.7 },
  drizzle: { cloudCover: 0.9, mist: 0.55, rain: 0.72, wetness: 0.92, wind: 0.4, visibility: 0.52, light: 0.58 },
  "after-rain": { cloudCover: 0.56, mist: 0.58, rain: 0.02, wetness: 0.88, wind: 0.16, visibility: 0.65, light: 0.82 },
};

const PRESET_HOURS: Record<TimePreset, number> = {
  dawn: 5.75,
  morning: 7.15,
  noon: 12.2,
  "golden-hour": 17.15,
  night: 21.2,
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;
const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [mix(a[0], b[0], t), mix(a[1], b[1], t), mix(a[2], b[2], t)];

function daylightAmount(hour: number): number {
  return clamp01(Math.sin(((hour - 5.35) / 13.3) * Math.PI));
}

export function resolveEnvironmentHour(preset: TimePreset, elapsed: number, timeFlow: boolean): number {
  const base = PRESET_HOURS[preset];
  if (!timeFlow || preset === "night") return base;

  const dayStart = 5.75;
  const dayLength = 12.25;
  return dayStart + ((((base - dayStart) + elapsed * 0.018) % dayLength) + dayLength) % dayLength;
}

export function createEnvironmentTarget(
  weather: WeatherKind,
  preset: TimePreset,
  elapsed = 0,
  timeFlow = false,
): EnvironmentState {
  const profile = WEATHER[weather];
  const hour = resolveEnvironmentHour(preset, elapsed, timeFlow);
  const daylight = daylightAmount(hour);
  const dawnWarmth = clamp01(1 - Math.abs(hour - 6.35) / 2.35);
  const eveningWarmth = clamp01(1 - Math.abs(hour - 17.25) / 2.5);
  const warmth = Math.max(dawnWarmth, eveningWarmth);
  const night = 1 - daylight;
  const visibility = profile.visibility;

  const nightTop: Rgb = [0.018, 0.032, 0.052];
  const dayTop: Rgb = mixRgb([0.32, 0.39, 0.41], [0.43, 0.56, 0.63], 1 - profile.cloudCover);
  const nightHorizon: Rgb = [0.06, 0.082, 0.09];
  const dayHorizon: Rgb = mixRgb([0.53, 0.59, 0.58], [0.7, 0.76, 0.72], 1 - profile.cloudCover);
  const warmHorizon: Rgb = [0.88, 0.64, 0.46];
  const horizon = mixRgb(mixRgb(nightHorizon, dayHorizon, daylight), warmHorizon, warmth * 0.24);
  const top = mixRgb(nightTop, dayTop, daylight);
  const fogColor = mixRgb(top, horizon, 0.72 + profile.mist * 0.18);
  const sunColor = mixRgb([0.7, 0.78, 0.9], [1, 0.95, 0.84], daylight);
  const warmedSun = mixRgb(sunColor, [1, 0.68, 0.44], warmth * 0.42);

  return {
    hour,
    sunElevation: mix(-0.18, 1.05, daylight),
    sunAzimuth: ((hour - 6) / 12) * Math.PI - Math.PI * 0.55,
    sunIntensity: Math.max(0.02, daylight * 2.8 * profile.light),
    hemisphereIntensity: mix(0.1, 1.5, daylight) * mix(0.82, 1, profile.light),
    cloudCover: profile.cloudCover,
    mist: profile.mist,
    rain: profile.rain,
    wetness: profile.wetness,
    wind: profile.wind,
    fireflies: preset === "night" ? 1 : clamp01((0.16 - daylight) * 7),
    fogNear: mix(72, 8, profile.mist) * visibility,
    fogFar: mix(420, 88, profile.mist) * visibility,
    skyTop: top,
    skyHorizon: horizon,
    fogColor,
    sunColor: warmedSun,
    waterColor: mixRgb([0.065, 0.13, 0.135], [0.21, 0.3, 0.27], daylight * (1 - profile.cloudCover * 0.32)),
  };
}

export function interpolateEnvironmentState(a: EnvironmentState, b: EnvironmentState, t: number): EnvironmentState {
  const amount = clamp01(t);
  if (amount === 0) return a;
  if (amount === 1) return b;
  return {
    hour: mix(a.hour, b.hour, amount),
    sunElevation: mix(a.sunElevation, b.sunElevation, amount),
    sunAzimuth: mix(a.sunAzimuth, b.sunAzimuth, amount),
    sunIntensity: mix(a.sunIntensity, b.sunIntensity, amount),
    hemisphereIntensity: mix(a.hemisphereIntensity, b.hemisphereIntensity, amount),
    cloudCover: mix(a.cloudCover, b.cloudCover, amount),
    mist: mix(a.mist, b.mist, amount),
    rain: mix(a.rain, b.rain, amount),
    wetness: mix(a.wetness, b.wetness, amount),
    wind: mix(a.wind, b.wind, amount),
    fireflies: mix(a.fireflies, b.fireflies, amount),
    fogNear: mix(a.fogNear, b.fogNear, amount),
    fogFar: mix(a.fogFar, b.fogFar, amount),
    skyTop: mixRgb(a.skyTop, b.skyTop, amount),
    skyHorizon: mixRgb(a.skyHorizon, b.skyHorizon, amount),
    fogColor: mixRgb(a.fogColor, b.fogColor, amount),
    sunColor: mixRgb(a.sunColor, b.sunColor, amount),
    waterColor: mixRgb(a.waterColor, b.waterColor, amount),
  };
}

export function smoothEnvironmentState(
  current: EnvironmentState,
  target: EnvironmentState,
  delta: number,
  responseSeconds = 2.8,
): EnvironmentState {
  if (delta <= 0) return current;
  const amount = 1 - Math.exp(-delta / Math.max(0.001, responseSeconds));
  return interpolateEnvironmentState(current, target, amount);
}
